import { useState } from 'react';
import { Building2, Plus, ChevronRight, ChevronDown, Users, Briefcase, Shield, Info } from 'lucide-react';
import { createOrganizationUnit } from '../../services/organizationService';

interface Props {
  nextStep: () => void;
  previousStep: () => void;
}

// Options for the "Parent (optional)" dropdown in the Add Unit modal.
// Update this list if your org tree data becomes dynamic.
const PARENT_OPTIONS = [
  { value: 'root', label: '— Root —' },
  { value: 'nova-tech', label: 'Nova Tech' },
  { value: 'nova', label: 'Nova' },
  { value: 'nova-tech-1', label: 'Nova Tech 1' },
  { value: 'baa', label: 'BAA' },
  { value: 'bcca', label: 'BCCA' },
];

const OrgStructure = ({ nextStep, previousStep }: Props) => {
  // ---- Add Unit modal state ----
  const [isAddUnitOpen, setIsAddUnitOpen] = useState(false);
  const [unitType, setUnitType] = useState('Company');
  const [parentId, setParentId] = useState('root');
  const [nameEn, setNameEn] = useState('');
  const [nameAr, setNameAr] = useState('');

  const openAddUnitModal = () => setIsAddUnitOpen(true);

  const closeAddUnitModal = () => {
    setIsAddUnitOpen(false);
    setUnitType('Company');
    setParentId('root');
    setNameEn('');
    setNameAr('');
  };

  const handleAddUnit = async () => {
    try {
      const payload = {
        type: unitType,
        parent_id: parentId === 'root' ? null : parentId,
        name_en: nameEn,
        name_ar: nameAr,
      };

      const response = await createOrganizationUnit(payload);

      closeAddUnitModal();
    } catch (error) {
      console.error('Failed to create organization unit:', error);
    }
  };

  return (
    <div className="organization-wrapper">
      {/* Header */}
      <div className="org-structure-header">
        <div className="org-structure-text">
          <h2 className="org-structure-title">Org Structure</h2>
          <p className="org-structure-subtitle">Define your organizational hierarchy</p>
        </div>
      </div>

      {/* Info Banner */}
      <div className="org-structure-banner">
        <div className="org-structure-banner-icon">
          <Info />
        </div>

        <p className="org-structure-banner-text">
          Add the holding company at the root, then add subsidiary companies, and branches under each.
        </p>
      </div>

      {/* Tree Structure */}
      <div className="mt-8 border border-gray-200 rounded-2xl p-6 bg-gray-50">
        <div className="space-y-4">
          {/* Nova Tech - Holding */}
          <div>
            <div className="org-tree-row">
              <ChevronDown className="org-tree-dropdown" />

              <div className="org-tree-badge holding">
                <Building2 />
                <span>Holding</span>
              </div>

              <span className="org-tree-name">Nova Tech</span>
            </div>

            {/* Children Container */}
            <div className="ml-10 mt-3 space-y-3">
              {/* Nova Company */}
              <div className="org-tree-row org-tree-company">
                <ChevronDown className="org-tree-dropdown" />

                <div className="org-tree-badge company">
                  <Briefcase />
                  <span>Company</span>
                </div>

                <span className="org-tree-name">Nova</span>
              </div>

              {/* Nova Tech 1 Branch */}
              <div className="ml-10">
                <div className="org-tree-row org-tree-branch">
                  <ChevronDown className="org-tree-dropdown" />

                  <div className="org-tree-badge branch">
                    <Users />
                    <span>Branch</span>
                  </div>

                  <span className="org-tree-branch-name">Nova Tech 1</span>
                </div>

                {/* Department */}
                <div className="ml-10 mt-3">
                  <div className="org-tree-row org-tree-department">
                    <div className="org-tree-badge department">
                      <Shield />
                      <span>Department</span>
                    </div>

                    <span className="org-tree-department-name">IT &amp; Security</span>
                  </div>
                </div>
              </div>

              {/* BAA Company */}
              <div className="org-tree-row org-tree-company">
                <div className="org-tree-badge company">
                  <Briefcase />
                  <span>Company</span>
                </div>

                <span className="org-tree-company-baa-name">BAA</span>
              </div>

              {/* BCCA Company */}
              <div className="org-tree-row org-tree-company">
                <div className="org-tree-badge company">
                  <Briefcase />
                  <span>Company</span>
                </div>

                <span className="org-tree-company-bcca-name">BCCA</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Unit Button */}
      {!isAddUnitOpen && (
        <div className="org-add-unit-section">
          <button className="org-add-unit-btn" onClick={openAddUnitModal}>
            <Plus className="org-add-unit-icon" />
            <span className="org-add-unit-text">Add Unit</span>
          </button>
        </div>
      )}

      {/* Add Unit Form */}
      {isAddUnitOpen && (
        <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
            {/* Type */}
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">Type</label>
              <select
                value={unitType}
                onChange={(e) => setUnitType(e.target.value)}
                className="w-full h-11 rounded-lg border border-gray-300 px-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#18B58A]"
              >
                <option value="Holding">Holding</option>
                <option value="Company">Company</option>
                <option value="Branch">Branch</option>
                <option value="Department">Department</option>
              </select>
            </div>

            {/* Parent (optional) */}
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">Parent (optional)</label>
              <select
                value={parentId}
                onChange={(e) => setParentId(e.target.value)}
                className="w-full h-11 rounded-lg border border-gray-300 px-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#18B58A]"
              >
                {PARENT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Name (EN) */}
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">Name (EN)</label>
              <input
                type="text"
                value={nameEn}
                onChange={(e) => setNameEn(e.target.value)}
                placeholder="e.g. Saudi HQ"
                className="w-full h-11 rounded-lg border border-gray-300 px-3 text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#18B58A]"
              />
            </div>

            {/* Name (AR) */}
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">الاسم (AR)</label>
              <input
                type="text"
                value={nameAr}
                onChange={(e) => setNameAr(e.target.value)}
                placeholder="مثال: المقر"
                className="w-full h-11 rounded-lg border border-gray-300 px-3 text-left text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#18B58A]"
              />
            </div>
          </div>

          {/* Form Action Buttons */}
          <div className="flex gap-3 mt-8">
            <button
              onClick={handleAddUnit}
              className="bg-[#18B58A] hover:bg-[#14996F] text-white rounded-lg px-8 h-11 font-medium transition-colors"
            >
              Add Unit
            </button>
            <button
              onClick={closeAddUnitModal}
              className="border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg px-8 h-11 font-medium transition-colors flex items-center gap-2"
            >
              ← Cancel
            </button>
          </div>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex justify-between mt-12">
        <button
          onClick={previousStep}
          className="border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg px-8 h-11 font-medium transition-colors"
        >
          ← Back
        </button>

        <div className="flex gap-3">
          <button className="border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg px-8 h-11 font-medium transition-colors">
            Save &amp; Edit
          </button>
          <button
            onClick={nextStep}
            className="bg-[#18B58A] hover:bg-[#14996F] text-white rounded-lg px-10 h-11 font-medium transition-colors flex items-center gap-2"
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrgStructure;
